import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Form, InputGroup, ListGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getSupportKnowledge } from "./supportApi";
import { addMessage } from "./supportSlice";
import { setPageName } from "../login/loginSlice";
import { findAnswer } from "./localMatch";

// Natural-feeling reply delay so it reads like someone is actually
// looking the answer up, rather than an instant canned response.
const REPLY_DELAY_MIN_MS = 1800;
const REPLY_DELAY_MAX_MS = 3000;

export function Support(props) {
  const log = useSelector((state) => state.login);
  const support = useSelector((state) => state.support);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [typing, setTyping] = useState(false);
  const typingTimeout = useRef(null);

  useEffect(() => {
    if (!log.token) {
      navigate("/login");
      return;
    }
    dispatch(setPageName("Поддержка"));
    dispatch(getSupportKnowledge(log.token));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
    };
  }, []);

  function respondAfterDelay(buildMessage) {
    setTyping(true);
    const delay =
      REPLY_DELAY_MIN_MS + Math.random() * (REPLY_DELAY_MAX_MS - REPLY_DELAY_MIN_MS);
    typingTimeout.current = setTimeout(() => {
      dispatch(addMessage(buildMessage()));
      setTyping(false);
    }, delay);
  }

  function askDeterministic(fault) {
    if (typing) return;
    dispatch(
      addMessage({ who: "operator", text: fault.title + " — что делать?" })
    );
    respondAfterDelay(() => {
      const answer =
        "Возможные причины:\n" +
        fault.causes.map((c, i) => `${i + 1}. ${c}`).join("\n") +
        "\n\nСпособ устранения:\n" +
        fault.fixes.map((c, i) => `${i + 1}. ${c}`).join("\n");
      return { who: "assistant", text: answer, source: fault.source };
    });
  }

  function askCode(c) {
    if (typing) return;
    dispatch(
      addMessage({ who: "operator", text: `Что означает код чиллера «${c.code}»?` })
    );
    respondAfterDelay(() => ({
      who: "assistant",
      text: c.meaning,
      source: c.source,
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const q = question.trim();
    if (!q || typing) return;
    dispatch(addMessage({ who: "operator", text: q }));
    setQuestion("");

    respondAfterDelay(() => {
      const result = findAnswer(q, support.faults, support.codes);
      return {
        who: "assistant",
        text: result.text,
        source: result.source,
        isSafety: result.isSafety,
      };
    });
  }

  return (
    <Fragment>
      <div className="col-xl-8 col-lg-10 mx-auto">
        <h5 className="mb-3">Поддержка · MetalTec 1530В</h5>
        <div className="row g-3">
          <div className="col-md-4">
            <div className="mb-3">
              <div className="text-muted small text-uppercase mb-2">
                Частые неисправности
              </div>
              <ListGroup>
                {support.faults.map((f, i) => (
                  <ListGroup.Item
                    action
                    key={i}
                    onClick={() => askDeterministic(f)}
                    disabled={typing}
                    style={{ cursor: typing ? "default" : "pointer" }}
                  >
                    <Badge bg="secondary" className="me-2">
                      {f.tag}
                    </Badge>
                    {f.title}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
            <div>
              <div className="text-muted small text-uppercase mb-2">
                Коды чиллера
              </div>
              <div className="d-flex flex-wrap gap-2">
                {support.codes.map((c, i) => (
                  <Button
                    key={i}
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => askCode(c)}
                    disabled={typing}
                  >
                    {c.code}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div
              className="border rounded p-3 mb-2"
              style={{ minHeight: 380, maxHeight: 480, overflowY: "auto" }}
            >
              {support.messages.length === 0 && !typing && (
                <div className="text-muted">
                  Выберите неисправность слева или опишите вопрос своими словами ниже.
                </div>
              )}
              {support.messages.map((m, i) => (
                <div
                  key={i}
                  className={
                    "mb-2 d-flex " +
                    (m.who === "operator"
                      ? "justify-content-end"
                      : "justify-content-start")
                  }
                >
                  <div
                    className={
                      "p-2 rounded " +
                      (m.who === "operator"
                        ? "bg-dark text-white"
                        : "bg-light border")
                    }
                    style={{ maxWidth: "85%", whiteSpace: "pre-wrap" }}
                  >
                    {m.isSafety && (
                      <div className="text-danger fw-bold small mb-1">
                        ВНИМАНИЕ · БЕЗОПАСНОСТЬ
                      </div>
                    )}
                    {m.text}
                    {m.source && (
                      <div className="small text-muted mt-2 border-top pt-1">
                        Источник: {m.source}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="mb-2 d-flex justify-content-start">
                  <div className="p-2 rounded bg-light border support-typing">
                    <span className="support-typing-dot">•</span>
                    <span className="support-typing-dot">•</span>
                    <span className="support-typing-dot">•</span>
                  </div>
                </div>
              )}
            </div>
            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <Form.Control
                  placeholder="Опишите ошибку или вопрос оператора…"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  disabled={typing}
                />
                <Button type="submit" variant="dark" disabled={typing}>
                  Спросить
                </Button>
              </InputGroup>
              <div className="form-text">
                Ответы составлены по руководству по эксплуатации MetalTec 1530В.
              </div>
            </Form>
          </div>
        </div>
      </div>
      <style>{`
        .support-typing-dot {
          display: inline-block;
          font-size: 22px;
          line-height: 1;
          opacity: 0.25;
          animation: supportTypingBlink 1.2s infinite;
        }
        .support-typing-dot:nth-child(2) { animation-delay: 0.2s; }
        .support-typing-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes supportTypingBlink {
          0%, 60%, 100% { opacity: 0.25; }
          30% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .support-typing-dot { animation: none; opacity: 1; }
        }
      `}</style>
    </Fragment>
  );
}

export default Support;
